/**
 * Workflow Schedules API
 * Scheduled workflow execution management endpoints
 * NOTE: Matches backend at /v1/admin/workflow-schedules/*
 */

import apiClient from './client';
import type {
  WorkflowSchedule,
  ScheduleCreateRequest,
  ScheduleUpdateRequest,
  ScheduleListResponse,
  ScheduleToggleResponse,
  ScheduleRunResponse,
  ScheduleListParams,
} from '@/types/workflowSchedule';

export const workflowSchedulesApi = {
  /**
   * List workflow schedules
   */
  list: async (params?: ScheduleListParams): Promise<ScheduleListResponse> => {
    const response = await apiClient.get('/workflow-schedules', { params });
    return response.data;
  },

  /**
   * Get a single schedule by ID
   */
  get: async (scheduleId: string): Promise<WorkflowSchedule> => {
    const response = await apiClient.get(`/workflow-schedules/${scheduleId}`);
    return response.data;
  },

  /**
   * Create a new schedule
   */
  create: async (data: ScheduleCreateRequest): Promise<WorkflowSchedule> => {
    const response = await apiClient.post('/workflow-schedules', data);
    return response.data;
  },

  /**
   * Update an existing schedule
   */
  update: async (scheduleId: string, data: ScheduleUpdateRequest): Promise<WorkflowSchedule> => {
    const response = await apiClient.put(`/workflow-schedules/${scheduleId}`, data);
    return response.data;
  },

  /**
   * Delete a schedule
   */
  delete: async (scheduleId: string): Promise<void> => {
    await apiClient.delete(`/workflow-schedules/${scheduleId}`);
  },

  /**
   * Toggle schedule enabled state
   * @param scheduleId - Schedule ID
   * @param enabled - Optional explicit state (undefined = toggle)
   */
  toggle: async (scheduleId: string, enabled?: boolean): Promise<ScheduleToggleResponse> => {
    const params = enabled !== undefined ? { enabled } : {};
    const response = await apiClient.post(`/workflow-schedules/${scheduleId}/toggle`, null, { params });
    return response.data;
  },

  /**
   * Manually trigger a schedule execution immediately
   */
  runNow: async (scheduleId: string): Promise<ScheduleRunResponse> => {
    const response = await apiClient.post(`/workflow-schedules/${scheduleId}/run`);
    return response.data;
  },

  /**
   * Get execution history for a schedule
   */
  getExecutions: async (
    scheduleId: string,
    params?: { limit?: number; offset?: number }
  ): Promise<{ total: number; executions: unknown[] }> => {
    const response = await apiClient.get(`/workflow-schedules/${scheduleId}/executions`, { params });
    return response.data;
  },
};
